import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    FILTER_EVENT,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {persistentStore, runtimeStore, hotelsStore} from '@/composables/store-accessor'
import {
    filters as blankFilters,
    hotelInfoRequest as blankHotelInfoRequest,
    hotelInfoResponse as blankHotelInfoResponse,
    searchRequest as blankSearchRequest,
    searchResponse as blankSearchResponse,
} from '@/utils/hotels/hotels-blank-states'

import {axiosInstance} from '@/utils/axios-accessor'
import {encodeSupplierCode, hotelNameToUrl, isEqual, later, matches} from '@/utils/helpers'
import {addSeconds} from 'date-fns'
import {appInstance} from '@/utils/app-accessor'
import {convertPrice} from '@/utils/filters'
import {sumPrice} from '@/utils/api-helpers'
import {partialSearchRequest, setSliderFilter} from '~src/utils/store-helpers'
import {srcHost} from '~src/utils/host-adapter'
import supplierIds from '@/api/suppliers.json'
import {updateRooms} from '@/utils/hotels/hotel-helpers'
import type {Ref} from 'vue'
import {updateWishlist as updateWishlistHotels} from '~/utils/hotels/hotel-helpers'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'

const runtimeFiltersBlank = () => ({
    map: {
        center: null,
        radius: 3000,
    },
})

function newSearchFn(
    stopSearch: Function,
    SET_FILTERED_OFFERS: Function,
    SET_RUNTIME_FILTERS: Function,
    searchRequest: any
) {
    stopSearch()
    SET_FILTERED_OFFERS([])
    SET_RUNTIME_FILTERS(runtimeFiltersBlank())
    hotelsStore.SET_SEARCH_RESPONSE(blankSearchResponse())
    hotelsStore.NEW_SEARCH(searchRequest)
}

function newSingleHotelSearchFn(stopSingleHotelSearch: Function, searchRequest: any) {
    stopSingleHotelSearch()
    hotelsStore.NEW_SINGLE_HOTEL_SEARCH(searchRequest)
}

function setSearchResponse(load: Function, searchResponse: any, rangeFilters: any) {
    //TODO Need to some refactoring

    rangeFilters.forEach((key: any) => {
        setSliderFilter(searchResponse, hotelsStore, key)
    })

    const {offers, ...sr} = searchResponse
    sr.offersCount = offers.length
    hotelsStore.SET_SEARCH_RESPONSE(sr)
    appInstance.$localForage.setItem('hotels', offers)
    load(offers)
}

async function partialSearch(
    SET_PARTIAL_SEARCH_RESULTS_CTS: Function,
    SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE: Function,
    partialSearchTimeoutInstance: Ref,
    load: Function,
    request: any,
    rangeFilters: any
) {
    const searchRequest = {...request}
    if (appInstance.$conf.public.searchAccommodationByDistance && searchRequest.latitude && searchRequest.longitude) {
        delete searchRequest.cityId
    }

    const {northEastLatitude, northEastLongitude, southWestLatitude, southWestLongitude} = searchRequest
    if (northEastLatitude && northEastLongitude && southWestLatitude && southWestLongitude) {
        delete searchRequest.cityId
    }

    const {searchKey} = await appInstance.$api.searchAccommodation.get(searchRequest)
    let searchResponse
    do {
        try {
            const cancelTokenSource = axiosInstance.CancelToken.source()
            SET_PARTIAL_SEARCH_RESULTS_CTS(cancelTokenSource)
            const {offersCount, processed} = await appInstance.$api.partialSearchStatus.get({searchKey})
            if (hotelsStore.searchResponse.offersCount === offersCount) {
                SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE(later(appInstance.$conf.public.search.partialResponseInterval))
                await partialSearchTimeoutInstance.value.promise
                searchResponse = {processed}
                if (!processed) continue
            }
            searchResponse = await appInstance.$api.partialSearchResults.get(
                partialSearchRequest({...searchRequest, searchKey}),
                cancelTokenSource.token
            )
            if (!searchResponse) break
            setSearchResponse(load, searchResponse, rangeFilters)
        } catch (e: any) {
            if (!e) {
                break
            } else if (e.status === 404) {
                //TODO Check 404 - searchKey has expired
                await partialSearch(
                    SET_PARTIAL_SEARCH_RESULTS_CTS,
                    SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
                    partialSearchTimeoutInstance,
                    load,
                    searchRequest,
                    rangeFilters
                )
                return
            } else {
                throw e
            }
        }
    } while (!searchResponse.processed)
    if (searchResponse.offers.length) {
        hotelsStore.SET_SEARCH_EXPIRATION_TIME(addSeconds(new Date(), appInstance.$conf.public.search.offersLifetime))
    }
    return searchResponse
}

export async function getSupplierIdByCode(supplierCode: any) {
    if (supplierCode.indexOf('company.') === 0) {
        return +supplierCode.replace('company.', '')
    } else {
        return supplierIds[supplierCode]
    }
}

//TODO need refactoring with partialSearch
async function singleHotelSearchFn(
    hotelInfoResponse: Ref,
    routeMap: Ref,
    SET_SINGLE_HOTEL_SEARCH_CTS: Function,
    SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE: Function,
    singleHotelSearchTimeoutInstance: Ref,
    searchRequest: any
) {
    const sr = {...searchRequest}
    if (hotelInfoResponse.value.hotelDescriptionInfo.localMappedHotelId) {
        delete sr.cityCode
        delete sr.supplierCode
        delete sr.hotelCode
        sr.hotelId = hotelInfoResponse.value.hotelDescriptionInfo.localMappedHotelId
    } else if (hotelInfoResponse.value.hotelDescriptionInfo.hotelId) {
        delete sr.cityCode
        delete sr.supplierCode
        delete sr.hotelCode
        sr.hotelId = hotelInfoResponse.value.hotelDescriptionInfo.hotelId
    } else if (sr.hotelCode && sr.cityCode) {
        delete sr.cityId
        delete sr.hotelId
    }

    //Workaround for medistays
    if (sr.cityId && routeMap.value.originalCityId) {
        sr.cityId = routeMap.value.originalCityId
    }

    const {northEastLatitude, northEastLongitude, southWestLatitude, southWestLongitude} = sr
    if (northEastLatitude && northEastLongitude && southWestLatitude && southWestLongitude) {
        delete sr.cityId
    }

    const {searchKey} = await appInstance.$api.searchAccommodation.get(sr)
    let searchResponse
    do {
        try {
            const cancelTokenSource = axiosInstance.CancelToken.source()
            SET_SINGLE_HOTEL_SEARCH_CTS(cancelTokenSource)
            const {offersCount, processed} = await appInstance.$api.partialSearchStatus.get({searchKey})
            if (hotelsStore.singleHotelSearchResponse.offers.length === offersCount) {
                SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE(later(appInstance.$conf.public.search.partialResponseInterval))
                await singleHotelSearchTimeoutInstance.value.promise
                searchResponse = {processed}
                if (!processed) continue
            }
            searchResponse = await appInstance.$api.partialSearchResults.get(
                {...sr, searchKey},
                cancelTokenSource.token
            )
            if (!searchResponse) break
            hotelsStore.SET_SINGLE_HOTEL_SEARCH_RESPONSE(searchResponse)
        } catch (e: any) {
            if (!e) {
                break
            } else if (e.status === 404) {
                //TODO Check 404 - searchKey has expired
                await singleHotelSearchFn(
                    hotelInfoResponse,
                    routeMap,
                    SET_SINGLE_HOTEL_SEARCH_CTS,
                    SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE,
                    singleHotelSearchTimeoutInstance,
                    searchRequest
                )
                return
            } else {
                throw e
            }
        }
    } while (!searchResponse.processed)
    if (searchResponse.offers.length) {
        hotelsStore.SET_SINGLE_HOTEL_SEARCH_EXPIRATION_TIME(
            addSeconds(new Date(), appInstance.$conf.public.search.offersLifetime)
        )
    }
    return searchResponse
}

// TODO Need investigate usage and reduce sumOffer item for multi-room case
export const minPriceRoomOfferFn = ({rooms}: any) => {
    //for khg
    const roomsSorted = [...rooms].sort(b => ('marketPrice' in b ? 1 : -1))

    if (!roomsSorted[0].price) {
        const priceKey = appInstance.$conf.public.packagesNotDeltaPrice ? 'notDeltaPrice' : 'deltaPrice'
        return roomsSorted.reduce(
            (a, b) => (a[priceKey].amount < b[priceKey].amount || b[priceKey].amount === undefined ? a : b),
            roomsSorted[0]
        )
    }
    const searchRoomsCount = hotelsStore.searchRequest.rooms.length,
        currency = persistentStore.getCurrency(rooms[0]?.price.currency)
    if (searchRoomsCount === 1)
        return roomsSorted
            .filter(room => !room.soldOut)
            .reduce((a, b) => (a.price.amount < b.price.amount ? a : b), roomsSorted[0])
    const sumOffer: any = {price: {amount: 0, currency, loyaltyAmount: 0}, available: false}
    for (let rph = 1; rph <= searchRoomsCount; rph++) {
        const minPriceRoom = roomsSorted
            .filter(
                room =>
                    !room.soldOut &&
                    room.groupedOffers.findIndex((groupedOffer: any) => groupedOffer.rph === rph) !== -1
            )
            .reduce((a, b) => (convertPrice(a.price).amount < convertPrice(b.price).amount ? a : b), {
                price: {amount: Infinity, currency},
            })
        const minPrice = minPriceRoom.price
        if (minPrice.amount !== Infinity) sumOffer.price.amount += convertPrice(minPrice).amount
        if (minPrice.taxesAndFeesExcluded) {
            if (!sumOffer.price.taxesAndFeesExcluded) {
                sumOffer.price.taxesAndFeesExcluded = minPrice.taxesAndFeesExcluded
            } else {
                sumOffer.price.taxesAndFeesExcluded = [
                    ...sumOffer.price.taxesAndFeesExcluded,
                    ...minPrice.taxesAndFeesExcluded,
                ]
            }
        }
        if (minPrice.taxesAndFeesIncludedSum) {
            if (!sumOffer.price.taxesAndFeesIncludedSum) {
                sumOffer.price.taxesAndFeesIncludedSum = {...minPrice.taxesAndFeesIncludedSum}
            } else {
                sumOffer.price.taxesAndFeesIncludedSum = sumPrice([
                    sumOffer.price.taxesAndFeesIncludedSum,
                    minPrice.taxesAndFeesIncludedSum,
                ])
            }
        }
        if (minPriceRoom.available) {
            sumOffer.available = minPriceRoom.available
        }
        sumOffer.price.loyaltyAmount += minPrice.loyaltyAmount
    }
    return sumOffer
}

export {newSearchFn, partialSearch}
export const setupHotelsRuntimeStore = () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {country, loadCountry} = productRuntimeStore
    //TODO Rename to runtimeOffers (not only filtered - sorted)
    const filteredOffers: Ref<any[]> = shallowRef([])
    const similarOffers: Ref<any[]> = ref([])

    const searchActiveCount = ref(0)
    const partialSearchResultsCTS: Ref = ref(null)
    const partialSearchTimeoutInstance: Ref = ref(null)

    const singleHotelSearchActiveCount = ref(0)
    const singleHotelSearchCTS: Ref = ref(null)
    const singleHotelSearchTimeoutInstance: Ref = ref(null)

    const updateActiveOffers: Ref<any[]> = ref([])
    const bookingActive = ref(false)
    const city: Ref = ref({})
    const hotelInfoRequest = ref(blankHotelInfoRequest())
    const hotelInfoResponse = ref(blankHotelInfoResponse())
    const hotelsInfo = ref<Map<string, ReturnType<typeof blankHotelInfoResponse>>>(new Map())

    const filterActiveCount = ref(0)
    const runtimeFilters = ref(runtimeFiltersBlank())

    const ownProductPaxes: Ref<any[]> = ref([])
    const routeMap: Ref = ref({cityId: null, hotelId: null})

    function START_FILTER() {
        filterActiveCount.value++
    }

    function STOP_FILTER() {
        filterActiveCount.value--
    }

    function SET_RUNTIME_FILTERS(filters: any) {
        runtimeFilters.value = filters
    }

    function SET_CITY(_city: any) {
        city.value = _city
    }

    function SET_BOOKING_ACTIVE(active: any) {
        bookingActive.value = active
    }

    function START_SEARCH() {
        searchActiveCount.value++
    }

    function STOP_SEARCH() {
        searchActiveCount.value--
        partialSearchResultsCTS.value = null
        partialSearchTimeoutInstance.value = null
    }

    function START_SINGLE_HOTEL_SEARCH() {
        singleHotelSearchActiveCount.value++
    }

    function STOP_SINGLE_SEARCH() {
        singleHotelSearchActiveCount.value--
        singleHotelSearchCTS.value = null
        singleHotelSearchTimeoutInstance.value = null
    }

    function SET_FILTERED_OFFERS(_filteredOffers: any) {
        filteredOffers.value = _filteredOffers
    }

    function SET_SIMILAR_OFFERS(_similarOffers: any) {
        similarOffers.value = _similarOffers
    }

    function ADD_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode}: any) {
        updateActiveOffers.value.push({supplierCode, cityCode, hotelCode})
    }

    function REMOVE_UPDATE_ROOMS_ACTIVE({supplierCode, cityCode, hotelCode}: any) {
        updateActiveOffers.value = updateActiveOffers.value.filter(
            item => !matches(item, {supplierCode, cityCode, hotelCode})
        )
    }

    function UPDATE_ROOMS({supplierCode, cityCode, hotelCode, rooms}: any) {
        const hotel = filteredOffers.value.find(
            offer => offer.supplierCode === supplierCode && offer.cityCode === cityCode && offer.hotelCode === hotelCode
        )
        if (!hotel) return
        hotel.rooms = rooms
        hotel.updatedRooms = true
    }

    function UPDATE_WISHLIST({supplierCode, cityCode, hotelCode, isWishlist}: any) {
        const hotel = filteredOffers.value.find(
            offer => offer.supplierCode === supplierCode && offer.cityCode === cityCode && offer.hotelCode === hotelCode
        )
        if (!hotel) return
        hotel.wishlist = isWishlist
    }

    function UPDATE_COORDINATES({supplierCode, cityCode, hotelCode, coordinates}: any) {
        const hotel = filteredOffers.value.find(
            offer => offer.supplierCode === supplierCode && offer.cityCode === cityCode && offer.hotelCode === hotelCode
        )
        if (!hotel) return
        hotel.coordinates = coordinates
    }

    function SET_PARTIAL_SEARCH_RESULTS_CTS(cancelTokenSource: any) {
        partialSearchResultsCTS.value = cancelTokenSource
    }

    function SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE(timeoutInstance: any) {
        partialSearchTimeoutInstance.value = timeoutInstance
    }

    function SET_SINGLE_HOTEL_SEARCH_CTS(cancelTokenSource: any) {
        singleHotelSearchCTS.value = cancelTokenSource
    }

    function SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE(timeoutInstance: any) {
        singleHotelSearchTimeoutInstance.value = timeoutInstance
    }

    async function loadCity(id: any) {
        try {
            const {cities} = await appInstance.$api.locations.get({id, limitCities: 1})
            city.value = cities[0]
        } catch (e) {
            city.value = {}
        }
    }

    async function loadHotelInfo(rq: any) {
        const key = getHotelsInfoKeyByRq(rq)
        try {
            const hotelInfo = await appInstance.$api.hotelInfo.get(rq)
            hotelInfoResponse.value = hotelInfo
            hotelInfoRequest.value = rq
            hotelsInfo.value.set(key, hotelInfo)
        } catch (e) {
            hotelInfoResponse.value = blankHotelInfoResponse()
            hotelInfoRequest.value = blankHotelInfoRequest()
            hotelsInfo.value.delete(key)
        }
    }

    // TODO [P3] remove and use loadHotelInfo & blankHotelInfoRequest
    async function loadHotelsInfoByOffer(offer: any, supplierCodesToOnlyLoaded: string[] = []) {
        try {
            const uniqueTourOperatorsRq = new Map()
            offer.rooms.forEach(({hotelSupplierCode, hotelCode}) => {
                if (
                    uniqueTourOperatorsRq.has(hotelSupplierCode) ||
                    (supplierCodesToOnlyLoaded.length && !supplierCodesToOnlyLoaded.includes(hotelSupplierCode))
                )
                    return

                const hotelInfoRq = {
                    supplierCode: hotelSupplierCode,
                    hotelCode: hotelCode,
                    cityCode: offer.cityCode,
                }
                uniqueTourOperatorsRq.set(hotelSupplierCode, {
                    rq: hotelInfoRq,
                    key: getHotelsInfoKeyByRq(hotelInfoRq),
                })
            })
            const promises = []
            uniqueTourOperatorsRq.forEach(async ({rq, key}) => {
                if (!hotelsInfo.value.has(key)) {
                    const promise = appInstance.$api.hotelInfo.get(rq).then(hotelInfo => {
                        hotelsInfo.value.set(key, hotelInfo)
                    })
                    promises.push(promise)
                }
            })
            await Promise.allSettled(promises)
        } catch {
            hotelsInfo.value.clear()
        }
    }

    function getHotelsInfoByRoomOffer(room: any, offer: any) {
        const key = getHotelsInfoKeyByRq({
            supplierCode: room.hotelSupplierCode,
            hotelCode: room.hotelCode,
            cityCode: offer.cityCode,
        })
        return hotelsInfo.value.get(key)
    }

    function getHotelsInfoKeyByRq(rq: ReturnType<typeof blankHotelInfoRequest>) {
        return rq.supplierCode + rq.hotelCode + rq.cityCode
    }

    async function loadOwnProductPaxes() {
        try {
            const paxIds = hotelsStore.bookingAdditionalOptions.reduce((paxIds: any[], roomOptions: any) => {
                if (roomOptions.alternativeMeals) {
                    roomOptions.alternativeMeals.forEach(({prices}: any) => {
                        prices.forEach(({paxId}: any) => {
                            paxIds.push(paxId)
                        })
                    })
                }
                return paxIds
            }, [])
            const paxesRs = await Promise.all(
                [...new Set(paxIds)].map(id => appInstance.$api.ownProductPaxes.get({id}))
            )
            ownProductPaxes.value = paxesRs.map(({paxes}) => paxes[0])
        } catch (e) {
            ownProductPaxes.value = []
        }
    }

    async function loadRouteMap(name: any) {
        const empty = {cityId: null, hotelId: null}
        try {
            const hotel = await axiosInstance.$get(srcHost(`/proxy/hotel/${name}`))
            routeMap.value = hotel ? hotel : empty
        } catch (e) {
            routeMap.value = empty
        }
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        appInstance.$workers.hotelsWorker.onmessage = ({data}: any) => {
            if (data.similarHotels) {
                SET_SIMILAR_OFFERS(data.similarHotels)
                return
            } else if (data === 'load') {
                console.timeEnd('load')
                if (
                    isEqual({...blankFilters(), price: hotelsStore.searchResponse.filters.price}, hotelsStore.filters)
                ) {
                    sort()
                } else {
                    filter()
                }
                return
            } else if (data === 'refresh') {
                console.timeEnd('load')
                return
            }
            STOP_FILTER()
            console.timeEnd('filter')
            if (filterActive.value) return
            //this.SET_FILTERED_OFFERS(JSON.parse(new TextDecoder().decode(data)))
            SET_FILTERED_OFFERS(data)
            EventBus.$emit(FILTER_EVENT)
        }
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        hotelsStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    function reset() {
        newSearchFn(stopSearch, SET_FILTERED_OFFERS, SET_RUNTIME_FILTERS, blankSearchRequest())
        newSingleHotelSearchFn(stopSingleHotelSearch, null)
        hotelsStore.RESET()
    }

    function newSearch() {
        newSearchFn(stopSearch, SET_FILTERED_OFFERS, SET_RUNTIME_FILTERS, blankSearchRequest())
    }

    function newSingleHotelSearch() {
        newSingleHotelSearchFn(stopSingleHotelSearch, null)
    }

    async function reload() {
        const promises = []
        if (hotelInfoRequest.value.hotelCode) promises.push(loadHotelInfo(hotelInfoRequest.value))
        if (city.value.id) promises.push(loadCity(city.value.id))
        await Promise.all(promises)
    }

    async function search(rq: any) {
        START_SEARCH()
        newSearchFn(stopSearch, SET_FILTERED_OFFERS, SET_RUNTIME_FILTERS, rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await partialSearch(
                SET_PARTIAL_SEARCH_RESULTS_CTS,
                SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
                partialSearchTimeoutInstance,
                load,
                rq,
                ['price']
            )
            // eslint-disable-next-line no-empty
        } catch (e) {
        } finally {
            STOP_SEARCH()
        }
    }

    function stopSearch() {
        if (partialSearchResultsCTS.value) partialSearchResultsCTS.value.cancel()
        if (partialSearchTimeoutInstance.value) partialSearchTimeoutInstance.value.cancel()
    }

    async function singleHotelSearch(rq: any) {
        START_SINGLE_HOTEL_SEARCH()
        newSingleHotelSearchFn(stopSingleHotelSearch, rq)
        EventBus.$emit(SEARCH_EVENT)
        try {
            await singleHotelSearchFn(
                hotelInfoResponse,
                routeMap,
                SET_SINGLE_HOTEL_SEARCH_CTS,
                SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE,
                singleHotelSearchTimeoutInstance,
                rq
            )
        } catch (e) {
            console.error(e)
        } finally {
            STOP_SINGLE_SEARCH()
        }
    }

    function stopSingleHotelSearch() {
        if (singleHotelSearchCTS.value) singleHotelSearchCTS.value.cancel()
        if (singleHotelSearchTimeoutInstance.value) singleHotelSearchTimeoutInstance.value.cancel()
    }

    async function updateWishlist({supplierCode, cityCode, hotelCode, isWishlist}: any) {
        const hotel = {supplierCode, cityCode, hotelCode}
        appInstance.$workers.hotelsWorker.postMessage({
            action: 'refreshWishlist',
            hotel,
            isWishlist,
        })
        const cachedOffers = await appInstance.$localForage.getItem('hotels')
        updateWishlistHotels(cachedOffers, hotel, isWishlist)
        appInstance.$localForage.setItem('hotels', cachedOffers)
        UPDATE_WISHLIST({supplierCode, cityCode, hotelCode, isWishlist})
    }

    async function updateRoomOffers({supplierCode, cityCode, hotelCode}: any) {
        const hotelInfoRq = {supplierCode, cityCode, hotelCode}
        ADD_UPDATE_ROOMS_ACTIVE(hotelInfoRq)
        try {
            const searchRequest: any = Object.assign({}, hotelsStore.searchRequest, {
                partialResponse: false,
                cityCode,
                hotelCode,
            })

            if (hotelInfoResponse.value.hotelDescriptionInfo.localMappedHotelId) {
                delete searchRequest.cityCode
                delete searchRequest.supplierCode
                delete searchRequest.hotelCode
                searchRequest.hotelId = hotelInfoResponse.value.hotelDescriptionInfo.localMappedHotelId
            } else if (hotelInfoResponse.value.hotelDescriptionInfo.hotelId) {
                delete searchRequest.cityCode
                delete searchRequest.supplierCode
                delete searchRequest.hotelCode
                searchRequest.hotelId = hotelInfoResponse.value.hotelDescriptionInfo.hotelId
            } else if (searchRequest.hotelCode && searchRequest.cityCode) {
                delete searchRequest.cityId
                delete searchRequest.hotelId
                searchRequest.supplierId = await getSupplierIdByCode(supplierCode)
            }

            //Workaround for medistays
            if (searchRequest.cityId && routeMap.value.originalCityId) {
                searchRequest.cityId = routeMap.value.originalCityId
            }

            const rs = await appInstance.$api.searchAccommodation.get(searchRequest)
            //TODO What to do if no offers here?
            if (!rs.offers.length) return
            const payload = {...hotelInfoRq, ...{rooms: rs.offers[0].rooms}}
            const cachedOffers = await appInstance.$localForage.getItem('hotels')
            updateRooms(cachedOffers, hotelInfoRq, payload.rooms)
            appInstance.$localForage.setItem('hotels', cachedOffers)
            console.time('load')
            appInstance.$workers.hotelsWorker.postMessage({
                action: 'refreshRooms',
                hotel: hotelInfoRq,
                rooms: clone(payload.rooms),
            })
            UPDATE_ROOMS(payload)
            appInstance.$workers.hotelsWorker.postMessage({
                action: 'filterNoSort',
                filters: clone({...hotelsStore.filters, ...runtimeFilters.value}),
                sortKey: hotelsStore.sortFnName,
            })
        } catch (e) {
            console.error(e)
        } finally {
            REMOVE_UPDATE_ROOMS_ACTIVE(hotelInfoRq)
        }
    }

    function load(offers = []) {
        //const offers = new TextEncoder().encode(JSON.stringify(hotelsStore.searchResponse.offers)).buffer
        //hotelsWorker.postMessage({action: 'load', offers}, [offers])
        console.time('load')
        appInstance.$workers.hotelsWorker.postMessage({action: 'load', offers})
    }

    async function filter() {
        START_FILTER()
        await nextTick()
        await timeout(0)
        console.time('filter')
        appInstance.$workers.hotelsWorker.postMessage({
            action: 'filter',
            filters: clone({...hotelsStore.filters, ...runtimeFilters.value}),
            sortKey: hotelsStore.sortFnName,
        })
    }

    function filterSimilar(info: any, radius = 3000) {
        const category = hotelsStore.category(info.stdCategory)
        const map = {
            center: {
                lat: info.latitude,
                lng: info.longitude,
            },
            radius,
        }
        appInstance.$workers.hotelsWorker.postMessage({
            action: 'similarHotels',
            filters: clone({...blankFilters(), price: [0, Infinity], category: [category], map}),
        })
    }

    function sort() {
        START_FILTER()
        console.time('filter')
        appInstance.$workers.hotelsWorker.postMessage({
            action: 'filter',
            filters: clone({...hotelsStore.filters, ...runtimeFilters.value}),
            sortKey: hotelsStore.sortFnName,
        })
    }

    const filterActive = computed(() => {
        return filterActiveCount.value > 0
    })

    const searchActive = computed(() => {
        return searchActiveCount.value > 0
    })

    const singleHotelSearchActive = computed(() => {
        return singleHotelSearchActiveCount.value > 0
    })

    const updateRoomsActive = computed(() => {
        return ({supplierCode, cityCode, hotelCode}: any) =>
            updateActiveOffers.value.findIndex(item => matches(item, {supplierCode, cityCode, hotelCode})) !== -1
    })

    const needUpdateRooms = computed(() => {
        return ({supplierCode, cityCode, hotelCode, updatedRooms}: any) =>
            appInstance.$conf.public.refreshHotelOffersSuppliers.includes(supplierCode) &&
            !updatedRooms &&
            !updateRoomsActive.value({supplierCode, cityCode, hotelCode})
    })

    const searchRequestTouristsJSON = computed(() => {
        return (searchRequest: any) => {
            return searchRequest.rooms.map((room: any) => {
                const groups = room.match(/^adults:(?<adults>\d)(,childrenAges:(?<childrenAges>(\d{1,2},?)+)$)?/).groups
                return {
                    adults: parseInt(groups.adults),
                    childrenAges: groups.childrenAges
                        ? groups.childrenAges.split(',').map((age: string) => parseInt(age))
                        : [],
                }
            })
        }
    })

    const searchRequestTouristsTotal = computed(() => {
        return (searchRequest: any) =>
            searchRequestTouristsJSON
                .value(searchRequest)
                .reduce((total: any, tourists: any) => total + tourists.adults + tourists.childrenAges.length, 0)
    })

    const searchRequestRoomTouristsCount = computed(() => {
        return (searchRequest: any, roomIndex: number) => {
            const tourists = searchRequestTouristsJSON.value(searchRequest)[roomIndex]
            return tourists.adults + tourists.childrenAges.length
        }
    })

    const roomQueryString = computed(() => {
        return ({adults, childrenAges}: any) => {
            let str = `adults:${adults}`
            if (childrenAges.length) {
                str += `,childrenAges:${childrenAges.join(',')}`
            }
            return str
        }
    })

    const hotelsPageLink = computed(() => {
        return (searchRequest: any) => {
            const {
                partialResponse,

                convertToCurrency,

                hotelId,

                hotelCode,

                cityCode,

                supplierCode,

                distance,

                latitude,

                longitude,
                ...query
            } = searchRequest
            return {name: 'hotels', query}
        }
    })

    const hotelPageLink = computed(() => {
        return (offer: any, searchRequest: any, hotelInfo: boolean) => {
            let {supplierCode, cityCode, hotelCode, name} = offer
            if (supplierCode) {
                supplierCode = encodeSupplierCode(supplierCode)
            }

            const {partialResponse, convertToCurrency, hotelId, distance, latitude, longitude, ...query} = searchRequest
            return {
                name: hotelInfo ? 'hotelInfo' : 'hotel',
                params: {name: hotelNameToUrl(name)},
                query: {...(!hotelInfo && query), supplierCode, cityCode, hotelCode},
            }
        }
    })

    const ownProduct = computed(() => {
        return (offer: any) => runtimeStore.ownProduct(offer.supplierCode)
    })

    const minPriceRoomOffer = computed(() => {
        return ({rooms}: any) => minPriceRoomOfferFn({rooms})
    })

    const mealTypePrice = computed(() => {
        return (prices: any, tourist: any) => {
            const filteredPrices = tourist.onExtrabed
                ? prices.filter(({placeType}: any) => placeType === 'Extra')
                : tourist.onWithoutPlace
                  ? prices.filter(({placeType}: any) => placeType === 'WithoutPlace')
                  : prices.filter(({placeType}: any) => placeType === 'Base')
            const paxCategory = tourist.type === 'adult' ? 'ADULTS' : 'CHILDREN'
            const ids =
                ownProductPaxes.value
                    .filter(
                        pax =>
                            pax.category === paxCategory &&
                            (paxCategory === 'ADULTS' || (tourist.age >= pax.ageFrom && tourist.age <= pax.ageTo))
                    )
                    .map(({id}) => id) || []
            return filteredPrices.find(price => ids.includes(price.paxId))?.price
        }
    })

    const findSameRoomOffer = computed(() => {
        return (newRooms: any, oldRoom: any) =>
            newRooms.find(
                ({name, mealTypeName, tariff}: any) =>
                    oldRoom.name === name && oldRoom.mealTypeName === mealTypeName && oldRoom.tariff === tariff
            )
    })

    const findRoomOffer = computed(() => {
        return (rooms: any, offerKey: any) =>
            rooms.find(
                (room: any) =>
                    room.groupedOffers.findIndex((groupedOffer: any) => groupedOffer.offerKey === offerKey) !== -1
            )
    })

    const findSameHotel = computed(() => {
        return (hotels: any, {hotelCode, supplierCode}: any) =>
            hotels.find((hotel: any) => hotel.hotelCode === hotelCode && hotel.supplierCode === supplierCode)
    })

    const findHotelByOfferKey = computed(() => {
        return (hotels: any, offerKey: any) =>
            hotels.find(
                ({rooms}: any) =>
                    rooms.findIndex(({groupedOffers}: any) => groupedOffers[0].offerKey === offerKey) !== -1
            )
    })

    return {
        filteredOffers,
        similarOffers,
        searchActiveCount,
        partialSearchResultsCTS,
        partialSearchTimeoutInstance,
        singleHotelSearchActiveCount,
        singleHotelSearchCTS,
        singleHotelSearchTimeoutInstance,
        updateActiveOffers,
        bookingActive,
        city,
        country,
        hotelInfoRequest,
        hotelInfoResponse,
        filterActiveCount,
        runtimeFilters,
        ownProductPaxes,
        routeMap,
        START_FILTER,
        STOP_FILTER,
        SET_RUNTIME_FILTERS,
        SET_CITY,
        SET_BOOKING_ACTIVE,
        START_SEARCH,
        STOP_SEARCH,
        START_SINGLE_HOTEL_SEARCH,
        STOP_SINGLE_SEARCH,
        SET_FILTERED_OFFERS,
        SET_SIMILAR_OFFERS,
        ADD_UPDATE_ROOMS_ACTIVE,
        REMOVE_UPDATE_ROOMS_ACTIVE,
        UPDATE_ROOMS,
        UPDATE_WISHLIST,
        UPDATE_COORDINATES,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        SET_PARTIAL_SEARCH_TIMEOUT_INSTANCE,
        SET_SINGLE_HOTEL_SEARCH_CTS,
        SET_SINGLE_HOTEL_SEARCH_TIMEOUT_INSTANCE,
        loadCity,
        loadCountry,
        loadHotelInfo,
        loadOwnProductPaxes,
        loadRouteMap,
        clientInit,
        changePrice,
        reset,
        newSearch,
        newSingleHotelSearch,
        reload,
        search,
        stopSearch,
        singleHotelSearch,
        stopSingleHotelSearch,
        updateWishlist,
        updateRoomOffers,
        load,
        filter,
        filterSimilar,
        sort,
        filterActive,
        searchActive,
        singleHotelSearchActive,
        updateRoomsActive,
        needUpdateRooms,
        searchRequestTouristsJSON,
        searchRequestTouristsTotal,
        searchRequestRoomTouristsCount,
        roomQueryString,
        hotelsPageLink,
        hotelPageLink,
        ownProduct,
        minPriceRoomOffer,
        mealTypePrice,
        findSameRoomOffer,
        findRoomOffer,
        findSameHotel,
        findHotelByOfferKey,
        hotelsInfo,
        getHotelsInfoKeyByRq,
        loadHotelsInfoByOffer,
        getHotelsInfoByRoomOffer,
    }
}

export const useHotelsRuntimeStore = defineStore('hotelsRuntime', setupHotelsRuntimeStore)

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useHotelsRuntimeStore, import.meta.hot))
